import React, { useState } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  InputNumber,
  DatePicker,
  Select,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import MainLayout from "../../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../../services/definedConstant";
import { rightAction } from "../../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../../treasuryContractBooking.config";
import { BOM } from "../../../../services/definedMethod";

// ── Mock securities — replace with real API call when endpoint is ready ───
// BOM.FetchReqAction({ session }, apiConfig.GetSecurities, (err, res) => {
//   if (!err) setSecurityOptions(res.map((s) => ({ label: s.name, value: s.id })));
// });
const MOCK_SECURITY_OPTIONS = [
  { label: "91-Day T-Bill (ID: 12)", value: 12 },
  { label: "182-Day T-Bill (ID: 13)", value: 13 },
  { label: "364-Day T-Bill (ID: 14)", value: 14 },
  { label: "5-Year FGN Bond (ID: 15)", value: 15 },
  { label: "10-Year FGN Bond (ID: 16)", value: 16 },
];

// BidQuoteType: 1 = Yield, 2 = Price, 3 = Discount Rate
const BID_QUOTE_TYPE_OPTIONS = [
  { label: "Yield (1)", value: 1 },
  { label: "Price (2)", value: 2 },
  { label: "Discount Rate (3)", value: 3 },
];

const AddAuctionLine = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const auctionId = location.state;
  const { userData } = props;
  const { session } = userData || {};

  const onFinish = (values) => {
    const body = {
      method: "POST",
      tenorDays: values.tenorDays,
      maturityDate: values.maturityDate?.format("YYYY-MM-DD"),
      offerAmount: values.offerAmount,
      bidQuoteType: values.bidQuoteType,
      dateFormat: "yyyy-MM-dd",
      locale: "en",
      session,
      // Optional fields — only include if filled
      ...(values.securityId != null && { securityId: values.securityId }),
      ...(values.minBidAmount != null && { minBidAmount: values.minBidAmount }),
      ...(values.maxBidAmount != null && { maxBidAmount: values.maxBidAmount }),
    };

    const url = `${apiConfig.GetAuctions}/auctions/${auctionId}/lines`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Failed to add auction line.",
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, "Auction line added successfully.");
      setIsLoading(false);
      navigate(-1);
    });
  };

  const Tip = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  return (
    <MainLayout pageItems={staticConfig.addAuctionLine}>
      <TopContent pageTitle={labelConfig.auction} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
              Add Auction Line
            </h3>

            <Form layout="vertical" form={form} onFinish={onFinish}>
              {/* Tenor Days & Maturity Date — mandatory */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Tenor (Days)"
                        tip="The number of days to maturity for this auction line."
                      />
                    }
                    name="tenorDays"
                    rules={[
                      { required: true, message: "Tenor is required" },
                      {
                        type: "number",
                        min: 1,
                        message: "Tenor must be at least 1 day",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 91"
                      min={1}
                      precision={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Maturity Date"
                        tip="The date this auction line matures."
                      />
                    }
                    name="maturityDate"
                    rules={[
                      { required: true, message: "Maturity date is required" },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%", height: 44 }}
                      format="YYYY-MM-DD"
                      placeholder="YYYY-MM-DD"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Offer Amount & Bid Quote Type — mandatory */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Offer Amount"
                        tip="The face value amount offered for this auction line."
                      />
                    }
                    name="offerAmount"
                    rules={[
                      { required: true, message: "Offer amount is required" },
                      {
                        type: "number",
                        min: 1,
                        message: "Amount must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 500,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Bid Quote Type"
                        tip="The method used to quote bids — Yield, Price, or Discount Rate."
                      />
                    }
                    name="bidQuoteType"
                    rules={[
                      { required: true, message: "Bid quote type is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={BID_QUOTE_TYPE_OPTIONS}
                      placeholder="Select Bid Quote Type"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Security — optional */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Security (Optional)"
                        tip="The security instrument linked to this auction line."
                      />
                    }
                    name="securityId"
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={MOCK_SECURITY_OPTIONS}
                      placeholder="Select Security"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Min Bid Amount & Max Bid Amount — optional */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Min Bid Amount (Optional)"
                        tip="The minimum amount a bidder can submit for this line."
                      />
                    }
                    name="minBidAmount"
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 1,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Max Bid Amount (Optional)"
                        tip="The maximum amount a bidder can submit for this line."
                      />
                    }
                    name="maxBidAmount"
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 600,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      marginRight: 12,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => form.submit()}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #BA24D5",
                      background: "#BA24D5",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Submit
                  </button>
                </Col>
              </Row>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(AddAuctionLine);
